import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Box, Chip } from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PercentIcon from '@mui/icons-material/Percent';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { LenderApproval, ApprovalBadge, ComparisonRowConfig, GqlLenderFee } from '../types';
import { COMPARISON_ROWS } from '../constants';
import { formatCellValue } from '../lib';
import { RankBadge } from './RankBadge';
import { LenderColumnHeader } from './LenderColumnHeader';

const ROW_ICONS: Record<string, React.ReactNode> = {
  approvedAmount: <AttachMoneyIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
  financeProgram: <AccountBalanceIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
  interestRate: <PercentIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
  downPayment: <PercentIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
  termLengthMonths: <CalendarTodayIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
  monthlyPayment: <ReceiptLongIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
  docFees: <AttachMoneyIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
  conditions: <ListAltIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
};

const FAVORABLE_CELL_BG = 'rgba(232, 245, 233, 0.6)';
const UNFAVORABLE_CELL_BG = 'rgba(255, 249, 196, 0.6)';

interface ApprovalTermsTableProps {
  approvals: LenderApproval[];
  badgesByRow: Record<string, Record<string, ApprovalBadge>>;
  onEdit?: (parsedDecisionId: string) => void;
}

function getCellValue(approval: LenderApproval, row: ComparisonRowConfig): string {
  if (row.key === 'conditions') {
    const count = approval.approvalTerms.conditions.length;
    return count === 0 ? '—' : String(count);
  }

  const termKey = row.key as keyof typeof approval.approvalTerms;
  const rawValue = approval.approvalTerms[termKey];

  return formatCellValue(rawValue as string | number | null, row.format);
}

// Collect all unique fee names across all lender approvals, preserving first-seen order.
function collectFeeNames(approvals: LenderApproval[]): string[] {
  const seen = new Set<string>();
  approvals.forEach(a =>
    a.approvalTerms.lenderFees.forEach(f => {
      if (f.feeName) seen.add(f.feeName);
    })
  );
  return Array.from(seen);
}

export const ApprovalTermsTable: React.FC<ApprovalTermsTableProps> = ({ approvals, badgesByRow, onEdit }) => {
  const feeNames = collectFeeNames(approvals);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  minWidth: 160,
                  py: 2,
                  px: 2.5,
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                  Approval Terms
                </Typography>
              </TableCell>
              {approvals.map((approval, index) => (
                <TableCell
                  key={approval.id}
                  align="center"
                  sx={{
                    minWidth: 180,
                    py: 2,
                    px: 2.5,
                    ...(approval.isExample && { bgcolor: 'rgba(255, 243, 224, 0.4)' }),
                    ...(index < approvals.length - 1 && {
                      borderRight: '1px solid',
                      borderColor: 'divider',
                    }),
                  }}
                >
                  <LenderColumnHeader
                    lenderName={approval.lenderName}
                    isExample={approval.isExample}
                    onClick={
                      !approval.isExample && approval.parsedDecisionId && onEdit
                        ? () => onEdit(approval.parsedDecisionId!)
                        : undefined
                    }
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {COMPARISON_ROWS.map((row, rowIndex) => (
              <TableRow
                key={row.key}
                sx={{
                  '&:last-child td': { borderBottom: 0 },
                }}
              >
                <TableCell
                  sx={{
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    py: 1.75,
                    px: 2.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {ROW_ICONS[row.key]}
                    <Typography variant="body2" fontWeight={500}>
                      {row.label}
                    </Typography>
                  </Box>
                </TableCell>
                {approvals.map((approval, colIndex) => {
                  const badge = badgesByRow[row.key]?.[approval.id];
                  const cellValue = getCellValue(approval, row);
                  const isNotProvided = cellValue === '\u2014';

                  const cellBg = badge ? (badge.favorable ? FAVORABLE_CELL_BG : UNFAVORABLE_CELL_BG) : undefined;

                  return (
                    <TableCell
                      key={approval.id}
                      align="center"
                      sx={{
                        backgroundColor: cellBg,
                        py: 1.75,
                        px: 2.5,
                        ...(colIndex < approvals.length - 1 && {
                          borderRight: '1px solid',
                          borderColor: 'divider',
                        }),
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexWrap: 'wrap',
                          gap: 0.5,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={isNotProvided ? 400 : 600}
                            color={isNotProvided ? 'text.disabled' : 'text.primary'}
                          >
                            {cellValue}
                          </Typography>
                          {isNotProvided && (
                            <Typography variant="caption" color="text.disabled">
                              {row.key === 'conditions' ? 'None' : 'Not provided'}
                            </Typography>
                          )}
                        </Box>
                        {badge && <RankBadge badge={badge} />}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {/* ── Lender Fees section ──────────────────────────────── */}
            {feeNames.length > 0 && (
              <>
                <TableRow>
                  <TableCell
                    colSpan={approvals.length + 1}
                    sx={{
                      py: 1,
                      px: 2.5,
                      bgcolor: 'grey.50',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
                    >
                      Lender Fees
                    </Typography>
                  </TableCell>
                </TableRow>

                {feeNames.map(feeName => (
                  <TableRow key={feeName} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell
                      sx={{
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        py: 1.75,
                        px: 2.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachMoneyIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight={500}>
                          {feeName}
                        </Typography>
                      </Box>
                    </TableCell>

                    {approvals.map((approval, colIndex) => {
                      const fee: GqlLenderFee | undefined = approval.approvalTerms.lenderFees.find(
                        f => f.feeName === feeName
                      );
                      const formattedAmount =
                        fee?.amount != null ? `$${Number(fee.amount).toLocaleString('en-US')}` : '—';
                      const isNotProvided = formattedAmount === '—';

                      return (
                        <TableCell
                          key={approval.id}
                          align="center"
                          sx={{
                            py: 1.75,
                            px: 2.5,
                            ...(colIndex < approvals.length - 1 && {
                              borderRight: '1px solid',
                              borderColor: 'divider',
                            }),
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={isNotProvided ? 400 : 600}
                              color={isNotProvided ? 'text.disabled' : 'text.primary'}
                            >
                              {formattedAmount}
                            </Typography>
                            {!isNotProvided && fee?.canBeFinanced != null && (
                              <Chip
                                label={fee.canBeFinanced ? 'Financeable' : 'Not financeable'}
                                size="small"
                                variant="outlined"
                                color={fee.canBeFinanced ? 'success' : 'default'}
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                            {isNotProvided && (
                              <Typography variant="caption" color="text.disabled">
                                Not provided
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
